/**
 * @fileoverview Sticky two-row header of the Task Tracker matrix: lesson columns on top, self / teacher /
 * deliverable sub-columns (with the AR-924 "Upload" action) below, then the Final Assessment pair.
 */

import { Fragment } from 'react'
import { Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { DeliverableUploadBadge } from './DeliverableUploadBadge'
import {
  FINAL_SUBCOLS,
  formatDate,
  headerCellStyle,
  stickyHeaderStyle,
  stickyTheadStyle,
  subHeaderStyle,
} from './tableHelpers'
import { subColCount } from './tableModel'
import type { TaskTrackLesson } from '../types'

const HEADER_LINE_GAP = 4

interface TaskTrackerTableHeadProps {
  lessons: TaskTrackLesson[]
  studentsCount: number
  /** Students still able to receive a teacher upload, per activity id (AR-924). */
  uploadableByActivity?: Record<string, number>
  onUploadForClassClick?: (activityId: string) => void
}

export function TaskTrackerTableHead({
  lessons, studentsCount, uploadableByActivity, onUploadForClassClick,
}: TaskTrackerTableHeadProps) {
  const { t, i18n } = useTranslation()

  return (
    <thead style={stickyTheadStyle}>
      <tr>
        <th rowSpan={2} style={stickyHeaderStyle}>
          <Text size="sm">{t('task_tracker.table.students_count', { count: studentsCount })}</Text>
        </th>
        {lessons.map(lesson => {
          const cols = subColCount(lesson)
          if (cols === 0) return null
          return (
            <th key={lesson.id} colSpan={cols} style={headerCellStyle}>
              <Stack spacing={HEADER_LINE_GAP}>
                {lesson.date && (
                  <Text size="xs" color="dimmed">{formatDate(lesson.date, i18n.language)}</Text>
                )}
                <Text size="sm" weight={600} lineClamp={2}>{lesson.title}</Text>
              </Stack>
            </th>
          )
        })}
        <th colSpan={FINAL_SUBCOLS} style={headerCellStyle}>
          <Stack spacing={HEADER_LINE_GAP}>
            <Text size="xs" color="dimmed" lineClamp={1}>&nbsp;</Text>
            <Text size="sm" weight={600} lineClamp={2}>{t('task_tracker.table.final_assessment')}</Text>
          </Stack>
        </th>
      </tr>
      <tr>
        {lessons.map(lesson => {
          const showActivityName = lesson.deliverableActivities.length > 1
          return (
            <Fragment key={lesson.id}>
              {lesson.hasAssessments && (
                <>
                  <th style={subHeaderStyle}>{t('task_tracker.table.self_assessment')}</th>
                  <th style={subHeaderStyle}>{t('task_tracker.table.teacher_assessment')}</th>
                </>
              )}
              {lesson.deliverableActivities.map(activity => (
                <th key={activity.id} style={subHeaderStyle}>
                  <Stack spacing={HEADER_LINE_GAP} align="center">
                    <Text size="xs" color="dimmed">{t('task_tracker.table.deliverable')}</Text>
                    {showActivityName && (
                      <Text size="xs" weight={500} lineClamp={2}>{activity.title}</Text>
                    )}
                    {onUploadForClassClick && (
                      <DeliverableUploadBadge
                        onClick={() => onUploadForClassClick(activity.id)}
                        disabled={uploadableByActivity?.[activity.id] === 0}
                      />
                    )}
                  </Stack>
                </th>
              ))}
            </Fragment>
          )
        })}
        <th style={subHeaderStyle}>{t('task_tracker.table.self_assessment')}</th>
        <th style={subHeaderStyle}>{t('task_tracker.table.teacher_assessment')}</th>
      </tr>
    </thead>
  )
}
